import { randomNumber } from '@packages/shared/core/common/utils/random';
import { CookiesService, HttpClient } from '@packages/shared/core/common/services';
import { ApiError, UnknownError } from '@packages/shared/core/common/errors';
import { UtmTagsService } from '@packages/shared/core/user/services';
import { Logger } from '@packages/shared/core/common/logger/interfaces';
import { normalizeError } from '@packages/shared/core/common/utils/errors';

export type GoogleAnalyticsAttributionParams = Partial<{
  ga: string;
}>;

export type GoogleAnalyticsTrackParams = Record<string, string | number>;

type GA4Event = {
  name: string;
  params?: Record<string, unknown>;
};

type GoogleAnalyticsCookieData = {
  sessionId: string;
  sessionNumber: string;
  sessionEngaged: string;
  lastHitTimestamp: string;
  joinTimer: string;
  enhancedUserIdLoggedInState: string;
  enhancedUserId: string;
  joinId: string;
};

export type GoogleAnalyticsServiceConfig = {
  googleAnalyticsId: string;
  measurementId: string;
  apiSecret: string;
  engagementTime: string;
  clientIdKey: string;
  sessionIdKey: string;
};

export class GoogleAnalyticsService {
  private readonly GA_COOKIE_KEYS = ['s', 'o', 'g', 't', 'j', 'l', 'h', 'd'] as const;

  private readonly GA_COOKIE_MAPPING = {
    s: 'sessionId',
    o: 'sessionNumber',
    g: 'sessionEngaged',
    t: 'lastHitTimestamp',
    j: 'joinTimer',
    l: 'enhancedUserIdLoggedInState',
    h: 'enhancedUserId',
    d: 'joinId',
  } as const;

  private readonly NOOP_COOKIE: GoogleAnalyticsCookieData = {
    sessionId: '',
    sessionNumber: '',
    sessionEngaged: '',
    lastHitTimestamp: '',
    joinTimer: '',
    enhancedUserIdLoggedInState: '',
    enhancedUserId: '',
    joinId: '',
  };

  constructor(
    private readonly httpClient: HttpClient,
    private readonly logger: Logger,
    private readonly utmTagsService: UtmTagsService,
    private readonly cookiesService: CookiesService,
    private readonly googleAnalyticsConfig: GoogleAnalyticsServiceConfig,
  ) {}

  async trackGA4(event: GA4Event): Promise<void> {
    const clientId = this.getClientId();
    const sessionId = this.getSessionId();

    try {
      await this.httpClient.post('mp/collect', {
        headers: {
          'content-type': 'text/plain',
        },
        params: {
          measurement_id: this.googleAnalyticsConfig.measurementId,
          api_secret: this.googleAnalyticsConfig.apiSecret,
        },
        data: {
          client_id: clientId,
          events: [
            {
              name: event.name,
              params: {
                session_id: sessionId,
                engagement_time_msec: this.googleAnalyticsConfig.engagementTime,
                ...event.params,
              },
            },
          ],
        },
      });
    } catch (e) {
      this.logger.error(normalizeError(e));
    }
  }

  getSessionId(): string | undefined {
    const sessionId = this.parseGoogleAnalyticsCookie().sessionId;
    if (!sessionId) return this.randomSessionId();
    return sessionId;
  }

  async track(params: GoogleAnalyticsTrackParams) {
    const utmTags = await this.utmTagsService.getUtmTags();
    try {
      await this.httpClient.post('collect', {
        params: {
          gclid: utmTags.gclid,
          cs: utmTags.utm_source,
          cm: utmTags.utm_medium,
          cn: utmTags.utm_campaign,
          cc: utmTags.utm_content,
          ck: utmTags.utm_term,
          z: this.randomNineDigitNumber(),
          tid: this.googleAnalyticsConfig.googleAnalyticsId,
          pa: 'checkout',
          t: 'event',
          v: 1,
          ...params,
        },
      });
    } catch (e) {
      const err = normalizeError(e);
      if (err instanceof ApiError) {
        this.logger.error(err);
      } else {
        this.logger.error(new UnknownError(err));
      }
    }
  }

  getAttributionParameters(): GoogleAnalyticsAttributionParams {
    const clients = window.ga?.getAll ? window.ga.getAll() : [];
    const desiredClient = clients[0];
    return {
      ga: desiredClient?.get('clientId'),
    };
  }

  private randomNineDigitNumber() {
    return randomNumber(1000000000, 9999999999);
  }

  private randomSessionId() {
    return this.randomNineDigitNumber().toString();
  }

  private getClientId(): string | undefined {
    const clientCookie = this.cookiesService.get(this.googleAnalyticsConfig.clientIdKey);
    if (!clientCookie) return this.randomClientId();
    return clientCookie.split('.').slice(2).join('.');
  }

  private randomClientId() {
    return `${this.randomNineDigitNumber()}.${this.randomNineDigitNumber()}`;
  }

  private parseGoogleAnalyticsCookie = (): GoogleAnalyticsCookieData => {
    try {
      const gaCookie = this.cookiesService.get(this.googleAnalyticsConfig.sessionIdKey);

      if (!gaCookie) {
        return this.NOOP_COOKIE;
      }

      const parts = gaCookie.split('.');
      const version = parts[0];

      if (version === 'GS1') {
        const values = parts.slice(2);
        const entries = this.GA_COOKIE_KEYS.map((key, index) => [this.GA_COOKIE_MAPPING[key], values[index] || '']);

        return Object.fromEntries(entries) as GoogleAnalyticsCookieData;
      }

      const data = parts[2];
      const entries = data
        .replace(/%24/g, '$')
        .split('$')
        .map(function (this: GoogleAnalyticsService, segment) {
          return [
            this.GA_COOKIE_MAPPING[segment[0] as keyof typeof this.GA_COOKIE_MAPPING] || segment[0],
            decodeURIComponent(segment.slice(1)),
          ];
        }, this);

      return Object.fromEntries(entries) as GoogleAnalyticsCookieData;
    } catch {
      return this.NOOP_COOKIE;
    }
  };
}
